export type GravityTypes =
  | 'no'
  | 'so'
  | 'ea'
  | 'we'
  | 'noea'
  | 'nowe'
  | 'soea'
  | 'sowe'
  | 'ce'
  | 'fp'

export interface ImgProxyParams {
  // See https://docs.imgproxy.net/#/generating_the_url_advanced
  /**
   * - fit: resizes the image while keeping aspect ratio to fit given size;
   * - fill: resizes the image while keeping aspect ratio to fill given size and cropping projecting parts;
   * - fill-down: same as fill, but if the resized image is smaller than the requested size, imgproxy will crop the result to keep the requested aspect ratio;
   * - force: resizes the image without keeping aspect ratio;
   * - auto: if both source and resulting dimensions have the same orientation (portrait or landscape), imgproxy will use fill. Otherwise, it will use fit.
   *
   * Default: `fit`
   */
  resizingType?: 'fit' | 'fill' | 'fill-down' | 'force' | 'auto'
  width?: number
  height?: number
  crop?: [number, number]
  /**
   * `type` - specifies the gravity type. Available values:
   *  - `no`: north (top edge);
   *  - `so`: south (bottom edge);
   *  - `ea`: east (right edge);
   *  - `we`: west (left edge);
   *  - `noea`: north-east (top-right corner);
   *  - `nowe`: north-west (top-left corner);
   *  - `soea`: south-east (bottom-right corner);
   *  - `sowe`: south-west (bottom-left corner);
   *  - `ce`: center.

   * `x_offset`, `y_offset` - (optional) specify gravity offset by X and Y axes.

   * Default: ce:0:0

   * Special gravities:
   * - `gravity:sm` - smart gravity. libvips detects the most “interesting” section of the image and considers it as the center of the resulting image. Offsets are not applicable here;
   * - `gravity:fp:%x:%y` - focus point gravity. x and y are floating point numbers between 0 and 1 that define the coordinates of the center of the resulting image. Treat 0 and 1 as right/left for x and top/bottom for y.
   */
  gravity?: `${GravityTypes}:${number}:${number}` | 'sm'
  /**
   * When set, imgproxy will multiply the image dimensions according to this
   * factor for HiDPI (Retina) devices. The value must be greater than 0.
   */
  dpr?: number
  /**
   * When enlarge is true, the image will be enlarged if it is smaller than the
   * given size. This is usually best left to the browser to minimize the file
   * size.
   */
  enlarge?: boolean
  /**
   * When extend is true, the image will be extended if it is smaller than the
   * given size. This may be useful when `object-fit: contain` is unavailable,
   * e.g. when you need to support IE11. In other cases it's recommended to
   * leave this off.
   */
  extend?: boolean
}

const baseUrl = process.env.REACT_APP_IMG_PROXY_URL
if (!baseUrl) {
  throw new Error('Missing required env var: REACT_APP_IMG_PROXY_URL')
}

function encode(str: string) {
  // url safe encoding from: https://npmdoc.github.io/node-npmdoc-urlsafe-base64/build/apidoc.html
  return Buffer.from(str)
    .toString('base64')
    .replace(/\+/g, '-') // Convert '+' to '-'
    .replace(/\//g, '_') // Convert '/' to '_'
    .replace(/=+$/, '') // Remove ending '='
}

function getSignature(str: string) {
  // imgproxy expects a signature in the url. We don't have a key/salt configured for our imgproxy, but have it set up to be restricted to a whitelist of hosts.
  // we can use any random string for the signature; encoding it as base64 will make it look hardened
  return encode(str)
}

function getImgProxyUrl(url: string, params?: ImgProxyParams) {
  const { crop, resizingType, width, height, dpr, enlarge, extend, gravity } =
    params ?? {}

  const parts = []

  if (resizingType) {
    parts.push(`rt:${resizingType}`)
  }
  if (width) {
    parts.push(`w:${width}`)
  }
  if (height) {
    parts.push(`h:${height}`)
  }
  if (dpr) {
    parts.push(`dpr:${dpr}`)
  }
  if (enlarge) {
    parts.push(`el:1`)
  }
  if (extend) {
    parts.push(`ex:1`)
  }

  if (crop && gravity) {
    parts.push(`crop:${crop[0]}:${crop[1]}:${gravity}`)
  } else if (crop) {
    parts.push(`crop:${crop[0]}:${crop[1]}`)
  } else if (gravity) {
    parts.push(`g:${gravity}`)
  }
  const partsStr = parts.join('/')
  const signature = getSignature(partsStr) // signature based on parts settings
  return `${baseUrl}/${signature}/${partsStr}${
    parts.length > 0 ? '/' : ''
  }${encode(url)}`
}

export default getImgProxyUrl
